import { PropsWithChildren, useEffect, useState } from "react";
import { NavigateOptions } from "react-router-dom";
import classnames from "classnames";
import { Image } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCircleExclamation,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import Header from "../Header/Header";
import PageFooter, { FooterAction } from "./PageFooter";
import { Size } from "./shared-types";

import styles from "./page.module.scss";
import LoadingContent from "../../Shared/LoadingContent";

const getMainContentSizeClass = (size: Size) => {
  switch (size) {
    case "full":
      return styles.contentSizeFull;
    case "wide":
      return styles.contentSizeWide;
    default:
      return styles.contentSizeNarrow;
  }
};

type Theme = "light" | "dark" | "volt" | "white" | "gray";

const getThemeClassName = (baseClassName: string) => (theme: Theme) => {
  switch (theme) {
    case "light":
      return styles[`${baseClassName}LightTheme`];
    case "dark":
      return styles[`${baseClassName}DarkTheme`];
    case "volt":
      return styles[`${baseClassName}VoltTheme`];
    case "gray":
      return styles[`${baseClassName}GrayTheme`];
    default:
      return styles[`${baseClassName}WhiteTheme`];
  }
};

const getMainContentContainerThemeClassName =
  getThemeClassName("contentContainer");
const getPageHeaderThemeClassName = getThemeClassName("pageHeader");

const getErrorBannerMessages = (message?: string | string[]) => {
  if (!message) {
    return ["Something went wrong, try again"];
  }

  if (typeof message === "string") {
    return [message];
  }

  return [...message];
};

export type PageProps = {
  menuButton?: boolean;
  fullMenu?: boolean;
  showHelpButton?: boolean;
  back?:
    | string
    | {
        route: string;
        options: NavigateOptions;
      }
    | (() => void);
  actions?: FooterAction | FooterAction[];
  footerLoading?: boolean;
  contentLoading?: boolean;
  theme?: Theme;
  size?: Size;
  progressProps?: {
    hasProgressTracker: boolean;
    progressPercent: number;
  };
  heading?: boolean;
  successMessage?: string;
  onSuccessDismiss?: () => void;
  errorLoading?: unknown;
  errorFromAction?: unknown;
  errorFromActionMessage?: string | string[];
  onActionErrorDismiss?: () => void;
  contentClassName?: string;
};

const Page = ({
  menuButton,
  fullMenu,
  showHelpButton,
  back,
  actions,
  contentLoading,
  footerLoading,
  children,
  theme = "light",
  size = "narrow",
  progressProps,
  heading,
  successMessage,
  onSuccessDismiss,
  errorLoading,
  errorFromAction,
  contentClassName,
  errorFromActionMessage,
  onActionErrorDismiss,
}: PropsWithChildren<PageProps>) => {
  const hasMenuButton = menuButton !== false;
  const hasFullMenu = fullMenu !== false;

  const [isErrorBannerDisplayed, setIsErrorBannerDisplayed] = useState(false);

  useEffect(() => {
    const shouldDisplayError = !!errorFromAction;
    setIsErrorBannerDisplayed(shouldDisplayError);
  }, [errorFromAction]);

  if (errorLoading) {
    return (
      <>
        {heading !== false && (
          <Header
            hasMenuButton={hasMenuButton}
            hasFullMenu={hasFullMenu}
            showHelpButton={showHelpButton}
            back={back}
            className={classnames(
              styles.pageHeader,
              styles.pageHeaderLightTheme,
            )}
            progressProps={progressProps}
            isClaimableDark={theme === "dark"}
          />
        )}
        <div
          className={classnames(
            styles.contentContainer,
            styles.contentContainerLightTheme,
          )}
        >
          <div
            className={classnames(
              styles.content,
              styles.contentSizeNarrow,
              styles.errorPage,
              {
                [styles.contentWithProgressBar]: progressProps,
                [styles.contentWithoutHeader]: heading === false,
              },
            )}
          >
            <div className={styles.heroContainer}>
              <h1 className={styles.errorHeading}>{"500 error"}</h1>
              <Image
                src="/images/product_illustrations/clipboard_purple.svg"
                w={205}
                h={205}
                alt="clipboard with checkbox"
              />
            </div>
            <p className={styles.errorMessage}>
              {"Something went wrong."}
              <br />
              {"We're taking a look to find a solution."}
            </p>
            <button
              type="button"
              className={styles.tryAgainButton}
              onClick={() => window.location.reload()}
            >
              {"Try again"}
            </button>
          </div>
        </div>
      </>
    );
  }

  const errorBannerMessages = getErrorBannerMessages(errorFromActionMessage);

  return (
    <>
      {heading !== false && (
        <Header
          hasMenuButton={hasMenuButton}
          hasFullMenu={hasFullMenu}
          showHelpButton={showHelpButton}
          back={back}
          className={classnames(
            styles.pageHeader,
            getPageHeaderThemeClassName(theme),
          )}
          progressProps={progressProps}
          isClaimableDark={theme === "dark"}
        />
      )}
      {isErrorBannerDisplayed && (
        <div
          className={classnames(styles.errorBannerContainer, {
            [styles.errorBannerUnderProgressBar]:
              progressProps?.hasProgressTracker,
          })}
        >
          <div
            className={classnames(styles.errorBanner, {
              [styles.errorBannerWide]: size === "wide",
            })}
          >
            <div className={styles.bannerContent}>
              <FontAwesomeIcon
                className={styles.alertIcon}
                icon={faCircleExclamation}
              />
              <div className={styles.bannerMessageWrapper}>
                {errorBannerMessages.map((message) => (
                  <p
                    className={styles.bannerText}
                    key={message}
                    dangerouslySetInnerHTML={{ __html: message }}
                  />
                ))}
              </div>
            </div>
            <button
              type="button"
              aria-label="close error"
              onClick={() => {
                setIsErrorBannerDisplayed(false);

                if (onActionErrorDismiss) {
                  onActionErrorDismiss();
                }
              }}
              className={styles.closeButton}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      )}
      {successMessage && !isErrorBannerDisplayed && (
        <div
          className={classnames(styles.successBannerContainer, {
            [styles.successBannerUnderProgressBar]:
              progressProps?.hasProgressTracker,
          })}
        >
          <div
            className={classnames(styles.successBanner, {
              [styles.successBannerWide]: size === "wide",
            })}
          >
            <div className={styles.bannerContent}>
              <FontAwesomeIcon className={styles.alertIcon} icon={faCheck} />
              <div className={styles.bannerMessageWrapper}>
                <p className={styles.bannerText}>{successMessage}</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="close error"
              onClick={() => {
                if (onSuccessDismiss) {
                  onSuccessDismiss();
                }
              }}
              className={styles.closeButton}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      )}
      <div
        className={classnames(
          styles.contentContainer,
          getMainContentContainerThemeClassName(theme),
        )}
      >
        <div
          className={classnames(styles.content, getMainContentSizeClass(size), {
            [styles.contentWithProgressBar]: progressProps,
            [styles.contentWithoutHeader]: heading === false,
            [styles.contentWithErrorBanner]: isErrorBannerDisplayed,
            [styles.contentWithSuccessBanner]:
              successMessage && !isErrorBannerDisplayed,
          })}
        >
          {contentLoading ? (
            <LoadingContent />
          ) : (
            <>
              <div
                className={classnames(
                  styles.passedContentWrapper,
                  contentClassName,
                )}
              >
                {children}
              </div>
              <PageFooter loading={footerLoading} actions={actions} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Page;
